import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Editorial "field notes" timeline — chapters render as an alternating
// vertical stack against a persistent purple ink rail.
export default function AboutStoryTimeline() {
  const { t } = useTranslation();
  const chapters = t("about.timeline.chapters", { returnObjects: true });
  const list = Array.isArray(chapters) ? chapters : [];

  return (
    <section
      className="relative py-24 md:py-32 bg-forest text-canvas noise overflow-hidden"
      data-testid="about-timeline"
    >
      {/* Ambient glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(700px 380px at 88% 6%, rgba(232,255,0,0.10), transparent 60%), radial-gradient(600px 320px at 4% 92%, rgba(124,58,237,0.35), transparent 60%)",
        }}
      />

      <div className="relative container-x">
        <div className="max-w-2xl">
          <div className="label-eyebrow !text-lime">{t("about.timeline.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium text-canvas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
            {t("about.timeline.title")}{" "}
            <span className="italic font-serif text-lime">
              {t("about.timeline.italic")}
            </span>
          </h2>
          <p className="mt-6 max-w-lg text-canvas/70 leading-relaxed">
            {t("about.timeline.body")}
          </p>
        </div>

        {/* Timeline stack */}
        <ol className="relative mt-16 md:mt-20 pl-6 md:pl-0 md:max-w-3xl md:mx-auto">
          {/* Rail */}
          <span
            aria-hidden
            className="absolute left-2 md:left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-lime/60 via-canvas/25 to-lime/0"
          />
          {list.map((c, i) => (
            <Chapter key={i} chapter={c} index={i} total={list.length} />
          ))}
        </ol>
      </div>
    </section>
  );
}

function Chapter({ chapter, index, total }) {
  const isRight = index % 2 === 1;
  const isLast = index === total - 1;
  return (
    <li
      className={`relative pb-14 md:pb-16 md:grid md:grid-cols-2 md:gap-10 ${
        isRight ? "md:[&>div:first-child]:col-start-2" : ""
      }`}
      data-testid={`about-timeline-chapter-${index}`}
    >
      {/* Node dot */}
      <span
        aria-hidden
        className={`absolute left-[3px] md:left-1/2 md:-translate-x-1/2 top-2 w-3.5 h-3.5 rounded-full ring-4 ring-forest ${
          isLast ? "bg-lime shadow-[0_0_20px_rgba(232,255,0,0.7)]" : "bg-canvas"
        }`}
      />

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1], delay: 0.05 * index }}
        className={`ml-8 md:ml-0 ${isRight ? "md:pl-10" : "md:pr-10 md:text-right"}`}
      >
        <div
          className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] tracking-[0.22em] uppercase font-bold ${
            isLast ? "bg-lime text-forest" : "bg-canvas/10 text-lime border border-lime/30"
          }`}
        >
          <span className="font-mono">{chapter.date}</span>
          <span className="w-1 h-1 rounded-full bg-current/60" />
          <span>{chapter.tag}</span>
        </div>
        <h3 className="mt-4 font-display font-medium text-canvas text-2xl md:text-3xl leading-[1.05] tracking-tight">
          {chapter.title}
        </h3>
        <p className="mt-3 text-canvas/70 leading-relaxed text-[15px] md:text-base">
          {chapter.body}
        </p>
      </motion.div>
    </li>
  );
}
